import { Link, router, usePage } from "@inertiajs/react"
import { type ReactNode, useEffect, useState } from "react"
import logoUrl from "~/images/nexgen-logo.png"
import type { AdminSharedProps } from "~/types/admin"
import "~/styles/admin.css"

/*
 * 管理画面のレイアウト。
 *
 * 公開サイトのトップと同じ紺地・ロゴ・英字の小見出しに揃える（色は
 * application.css の admin-*、形は styles/admin.css）。公開側は写真も本文も
 * 暗い地で見せるので、入稿の画面も同じ地で見たほうが仕上がりを判断しやすい。
 * 密度は公開側より上げる（一覧で 20 行が一画面に入るくらい）。
 */

type NavItem = { label: string; href: string; badge?: number }
type NavGroup = { label: string | null; items: NavItem[] }

function useNavigation(): NavGroup[] {
  const { props } = usePage<AdminSharedProps>()
  const counts = props.admin?.counts

  return [
    { label: null, items: [ { label: "ダッシュボード", href: "/admin" } ] },
    {
      label: "Contents",
      items: [
        { label: "製品", href: "/admin/products" },
        { label: "ソリューション", href: "/admin/solutions" },
        { label: "導入実績", href: "/admin/case_studies" },
        { label: "お知らせ", href: "/admin/articles" },
        { label: "課題", href: "/admin/issues" },
        { label: "代表者メッセージ", href: "/admin/representative_message/edit" },
      ],
    },
    {
      label: "Masters",
      items: [
        { label: "メーカー", href: "/admin/makers" },
        { label: "製品カテゴリ", href: "/admin/categories" },
        { label: "ソリューションカテゴリ", href: "/admin/solution_categories" },
        { label: "仕様項目", href: "/admin/spec_definitions" },
      ],
    },
    {
      label: "Inbox",
      items: [ { label: "問い合わせ", href: "/admin/inquiries", badge: counts?.unhandled_inquiries } ],
    },
    {
      label: "Settings",
      items: [
        { label: "メディア", href: "/admin/media_assets" },
        { label: "管理者", href: "/admin/administrators" },
      ],
    },
  ]
}

function Brand() {
  return (
    <Link href="/admin" className="admin-brand" aria-label="管理画面のダッシュボード">
      <span className="admin-brand-symbol">
        <img src={logoUrl} alt="" />
      </span>
      <span className="admin-brand-name">
        NEXGEN<small>ADMIN CONSOLE</small>
      </span>
    </Link>
  )
}

export default function AdminLayout({ children }: { children: ReactNode }) {
  const { url, props, flash } = usePage<AdminSharedProps>()
  const nav = useNavigation()
  const [menuOpen, setMenuOpen] = useState(false)

  useEffect(() => setMenuOpen(false), [url])

  useEffect(() => {
    document.title = `${props.meta.title ?? "管理画面"} | 管理画面`
  }, [props.meta.title])

  const isCurrent = (href: string) =>
    href === "/admin" ? url === "/admin" : url.startsWith(href)
  const currentGroup = nav.find((group) => group.items.some((item) => isCurrent(item.href)))

  const sidebar = (
    <nav className="flex h-full flex-col gap-7 overflow-y-auto px-4 py-6">
      <div className="px-2">
        <Brand />
      </div>

      <div className="flex flex-1 flex-col gap-6">
        {nav.map((group, index) => (
          <div key={group.label ?? `group-${index}`}>
            {group.label && <p className="admin-eyebrow px-2 pb-2">{group.label}</p>}
            <ul className="space-y-0.5">
              {group.items.map((item) => (
                <li key={item.href}>
                  <Link
                    href={item.href}
                    aria-current={isCurrent(item.href) ? "page" : undefined}
                    className={`flex items-center justify-between rounded-sm px-3 py-1.5 text-[13px] tracking-wide transition ${
                      isCurrent(item.href)
                        ? "admin-nav-current bg-white/[0.06] font-medium text-admin-fg"
                        : "text-admin-soft hover:bg-white/[0.03] hover:text-admin-fg"
                    }`}
                  >
                    <span>{item.label}</span>
                    {item.badge != null && item.badge > 0 && (
                      <span className="ml-2 rounded-full bg-rose-600 px-1.5 text-[11px] font-semibold text-white">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="border-t border-admin-line px-2 pt-4">
        <p className="text-xs text-admin-text">{props.admin?.administrator?.name ?? props.admin?.administrator?.email}</p>
        <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.13em] text-admin-faint">
          {props.admin?.administrator?.role_label}
        </p>
        <div className="mt-3 flex items-center gap-4 text-xs">
          <a href="/" target="_blank" rel="noopener noreferrer" className="text-admin-muted hover:text-signal-2">
            サイトを見る ↗
          </a>
          <button
            type="button"
            onClick={() => router.delete("/session")}
            className="text-admin-muted hover:text-signal-2"
          >
            ログアウト
          </button>
        </div>
      </div>
    </nav>
  )

  return (
    <div className="nx-admin min-h-screen bg-admin-ground text-admin-fg">
      <div className="lg:grid lg:grid-cols-[240px_minmax(0,1fr)]">
        <aside className="sticky top-0 hidden h-screen border-r border-admin-line bg-admin-sunken lg:block">
          {sidebar}
        </aside>

        {/* モバイル */}
        <div className="flex items-center justify-between border-b border-admin-line bg-admin-sunken px-4 py-3 lg:hidden">
          <Brand />
          <button type="button" onClick={() => setMenuOpen(true)} className="rounded p-1 text-admin-soft">
            <span className="sr-only">メニューを開く</span>
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
            </svg>
          </button>
        </div>
        {menuOpen && (
          <div className="fixed inset-0 z-50 flex lg:hidden">
            <div className="flex-1 bg-black/60" onClick={() => setMenuOpen(false)} aria-hidden="true" />
            <div className="w-64 border-l border-admin-line bg-admin-sunken">{sidebar}</div>
          </div>
        )}

        <main className="min-w-0 px-4 pb-10 pt-5 lg:px-10">
          <div className="mb-7 flex items-center justify-between gap-4 border-b border-admin-line pb-4">
            <p className="admin-eyebrow">
              Admin console{currentGroup?.label ? ` / ${currentGroup.label}` : ""}
            </p>
            <span className="hidden font-mono text-[10.5px] uppercase tracking-[0.13em] text-admin-faint sm:block">
              NEXGEN solution
            </span>
          </div>
          {flash.notice && (
            <p className="mb-4 rounded-sm border border-emerald-400/30 bg-emerald-400/10 px-4 py-2 text-sm text-emerald-200">
              {flash.notice}
            </p>
          )}
          {flash.alert && (
            <p className="mb-4 rounded-sm border border-rose-400/30 bg-rose-500/10 px-4 py-2 text-sm text-rose-200">
              {flash.alert}
            </p>
          )}
          {children}
        </main>
      </div>
    </div>
  )
}
